import { useState, useEffect } from 'react';
import { errorCode } from '@/components/analytics/LoadError';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, TrendingUp, Target, Lightbulb, RefreshCw, Loader2 } from 'lucide-react';
import { api } from '@/api/api-client';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import type { DialerCounters } from '@/api/endpoints/analytics';

interface AIPerformanceCoachProps {
    /** The period's totals from the server (W4): the coach reads every call, not the 500 loaded ones. */
    totals: DialerCounters | null;
}

/** The server has no model key (docs/api/errors.md, QA-W4-6). */
const AI_NOT_CONFIGURED = 'AI_NOT_CONFIGURED';

interface IntegrationsApi {
    Core: {
        InvokeLLM(args: { feature: 'performance_coach'; prompt: string; add_context_from_internet?: boolean }): Promise<string>;
    };
}

export default function AIPerformanceCoach({ totals }: AIPerformanceCoachProps) {
    const callCount = totals?.total ?? 0;
    const { t } = useTranslation(['pages']);
    const [analysis, setAnalysis] = useState<string | null>(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    // A failed analysis: the server code, kept apart from the analysis (DES-16).
    const [failure, setFailure] = useState<{ code?: string } | null>(null);
    const notConfigured = failure?.code === AI_NOT_CONFIGURED;

    const analyzePerformance = async () => {
        setIsAnalyzing(true);
        setFailure(null);
        try {
            // The figures of the screen (server totals, metric contracts):
            // connected = reached the agent, conversion = meetings / connected.
            const totalCalls = totals?.total ?? 0;
            const connectedCalls = totals?.connected ?? 0;
            const meetingsBooked = totals?.meetings ?? 0;
            const notInterested = totals?.notInterested ?? 0;
            const noAnswer = totals?.noAnswer ?? 0;

            const connectionRate = totalCalls > 0 ? ((connectedCalls / totalCalls) * 100).toFixed(1) : '0';
            const conversionRate = connectedCalls > 0 ? ((meetingsBooked / connectedCalls) * 100).toFixed(1) : '0';

            const avgDuration = totals && totals.durationCount > 0 ? Math.round(totals.durationSeconds / totals.durationCount) : 0;

            // Prepare context for AI
            const context = `Analysez les performances d'appels suivantes et donnez des conseils personnalisés et actionnables:

**Statistiques:**
- Total d'appels: ${totalCalls}
- Appels connectés: ${connectedCalls}
- Rendez-vous obtenus: ${meetingsBooked}
- Refus: ${notInterested}
- Sans réponse: ${noAnswer}
- Taux de connexion: ${connectionRate}%
- Taux de conversion: ${conversionRate}%
- Durée moyenne: ${Math.floor(avgDuration / 60)}min ${avgDuration % 60}s

**Demande:**
En tant que coach commercial expert, analysez ces données et fournissez:
1. Une évaluation globale de la performance (2-3 lignes)
2. 3 points forts identifiés
3. 3 axes d'amélioration prioritaires avec des conseils concrets et actionnables
4. Une recommandation stratégique personnalisée

Soyez direct, encourageant et ultra-pratique. Utilisez des emojis pour rendre le message dynamique.`;

            const result = await (api as { integrations: IntegrationsApi }).integrations.Core.InvokeLLM({
                feature: 'performance_coach',
                prompt: context,
                add_context_from_internet: false,
            });

            setAnalysis(result);
        } catch (error) {
            console.error('AI analysis failed:', error);
            setFailure({ code: errorCode(error) });
        } finally {
            setIsAnalyzing(false);
        }
    };

    // QA-W4-6: no analysis on load. Opening Performances used to call the
    // model at once (a paid call, and a 500 without a model key); it now
    // runs on demand only: the button, or the « triggerAICoachAnalysis » event.

    useEffect(() => {
        const handleTrigger = () => {
            analyzePerformance();
        };
        window.addEventListener('triggerAICoachAnalysis', handleTrigger);
        return () => window.removeEventListener('triggerAICoachAnalysis', handleTrigger);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totals]);

    return (
        <Card id="ai-performance-coach" className="bg-[var(--vf-accent-bg)] border-[var(--vf-accent-bd)]">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <div className="bg-[var(--vf-primary)] w-10 h-10 rounded-full flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-foreground">{t('pages:analytics.aiPerformanceCoach.title')}</h2>
                            <p className="text-xs text-muted-foreground font-normal">{t('pages:analytics.aiPerformanceCoach.subtitle')}</p>
                        </div>
                    </CardTitle>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={analyzePerformance}
                        disabled={isAnalyzing || callCount === 0}
                        aria-label={t('pages:analytics.aiPerformanceCoach.refreshLabel')}
                        className="border-[var(--vf-accent-bd)] hover:bg-[var(--vf-accent-bg)]"
                    >
                        <RefreshCw className={`w-4 h-4 ${isAnalyzing ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {isAnalyzing ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Loader2 className="w-12 h-12 text-[var(--vf-accent-text)] animate-spin mb-4" />
                        <p className="text-muted-foreground text-sm">{t('pages:analytics.aiPerformanceCoach.analyzing')}</p>
                        <p className="text-muted-foreground text-xs mt-1">{t('pages:analytics.aiPerformanceCoach.analyzingHint')}</p>
                    </div>
                ) : callCount === 0 ? (
                    <div className="text-center py-12">
                        <Target className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
                        <p className="text-muted-foreground">{t('pages:analytics.aiPerformanceCoach.noData')}</p>
                        <p className="text-muted-foreground text-sm mt-1">{t('pages:analytics.aiPerformanceCoach.noDataHint')}</p>
                    </div>
                ) : failure ? (
                    <div role="alert" className="flex flex-col items-center gap-2 py-8 text-center">
                        <p className="text-sm text-foreground">
                            {notConfigured
                                ? t('pages:analytics.aiPerformanceCoach.notConfigured')
                                : t('pages:analytics.aiPerformanceCoach.analysisError')}
                        </p>
                        {failure.code && <p className="font-vf-mono text-[11px] text-muted-foreground">{failure.code}</p>}
                        {!notConfigured && (
                            <Button type="button" variant="outline" size="sm" onClick={analyzePerformance}>
                                {t('pages:analytics.aiPerformanceCoach.launch')}
                            </Button>
                        )}
                    </div>
                ) : analysis ? (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4"
                    >
                        <div className="bg-[var(--vf-bg)] rounded-xl p-4 border border-[var(--vf-accent-bd)]">
                            <div className="prose prose-sm max-w-none prose-headings:text-foreground prose-p:text-foreground/80 prose-strong:text-[var(--vf-text-1)] prose-li:text-foreground/80">
                                <ReactMarkdown
                                    components={{
                                        h1: ({ children }) => <h3 className="text-base font-bold text-foreground mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[var(--vf-accent-text)]" />{children}</h3>,
                                        h2: ({ children }) => <h4 className="text-sm font-semibold text-foreground mt-4 mb-2 flex items-center gap-2"><Lightbulb className="w-4 h-4 text-[var(--vf-accent-text)]" />{children}</h4>,
                                        h3: ({ children }) => <h5 className="text-sm font-semibold text-foreground mt-3 mb-1">{children}</h5>,
                                        ul: ({ children }) => <ul className="space-y-2 my-2">{children}</ul>,
                                        li: ({ children }) => <li className="flex items-start gap-2 text-sm"><span className="text-[var(--vf-accent-text)] mt-1">•</span><span>{children}</span></li>,
                                        p: ({ children }) => <p className="text-sm leading-relaxed mb-2">{children}</p>,
                                        strong: ({ children }) => <strong className="font-semibold text-[var(--vf-accent-text)]">{children}</strong>,
                                    }}
                                >
                                    {analysis}
                                </ReactMarkdown>
                            </div>
                        </div>

                        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                            <Sparkles className="w-3 h-3" />
                            <span>{t('pages:analytics.aiPerformanceCoach.generatedBy')}</span>
                        </div>
                    </motion.div>
                ) : (
                    <div className="text-center py-8">
                        <Button
                            onClick={analyzePerformance}
                            className="bg-[var(--vf-primary)] hover:bg-[var(--vf-primary-hover)]"
                        >
                            <Sparkles className="w-4 h-4 mr-2" />
                            {t('pages:analytics.aiPerformanceCoach.launch')}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}